<template>
  <div class="friend-group-management">
    <div class="header">
      <h2>亲友团管理</h2>
      <div class="actions">
        <el-button type="primary" @click="handleCreateGroup">
          <el-icon><Plus /></el-icon>
          创建亲友关系
        </el-button>
        <el-button @click="refreshData">
          <el-icon><Refresh /></el-icon>
          刷新
        </el-button>
      </div>
    </div>

    <!-- 搜索和筛选 -->
    <el-card class="filter-card">
      <el-form :inline="true" :model="searchForm">
        <el-form-item label="搜索">
          <el-input v-model="searchForm.search" placeholder="用户姓名/手机号" clearable />
        </el-form-item>
        <el-form-item label="状态">
          <el-select v-model="searchForm.status" placeholder="请选择状态" clearable>
            <el-option label="待确认" value="pending" />
            <el-option label="已接受" value="accepted" />
            <el-option label="已拒绝" value="rejected" />
          </el-select>
        </el-form-item>
        <el-form-item label="分组类型">
          <el-select v-model="searchForm.groupType" placeholder="请选择类型" clearable>
            <el-option label="朋友" value="friend" />
            <el-option label="家人" value="family" />
            <el-option label="同事" value="colleague" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">
            <el-icon><Search /></el-icon>
            搜索
          </el-button>
          <el-button @click="resetSearch">
            <el-icon><Refresh /></el-icon>
            重置
          </el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 亲友关系列表 -->
    <el-card>
      <el-table :data="groupList" v-loading="loading" stripe border>
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column label="发起用户" width="180">
          <template #default="{ row }">
            <div v-if="row.user" class="user-info">
              <span>{{ row.user.realName || '未设置' }}</span>
              <span class="user-mobile">{{ row.user.mobile }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="目标用户" width="180">
          <template #default="{ row }">
            <div v-if="row.targetUser" class="user-info">
              <span>{{ row.targetUser.realName || '未设置' }}</span>
              <span class="user-mobile">{{ row.targetUser.mobile }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="relationshipType" label="关系类型" width="120">
          <template #default="{ row }">
            {{ getRelationshipTypeText(row.relationshipType) }}
          </template>
        </el-table-column>
        <el-table-column prop="groupType" label="分组" width="100">
          <template #default="{ row }">
            {{ getGroupTypeText(row.groupType) }}
          </template>
        </el-table-column>
        <el-table-column prop="relationshipStrength" label="亲密度" width="100">
          <template #default="{ row }">
            {{ getStrengthText(row.relationshipStrength) }}
          </template>
        </el-table-column>
        <el-table-column prop="totalHelpAmount" label="累计帮还金额" width="150">
          <template #default="{ row }">
            <span class="amount">¥{{ formatAmount(row.totalHelpAmount) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="helpCount" label="帮还次数" width="100">
          <template #default="{ row }">
            <el-tag>{{ row.helpCount || 0 }}次</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="100">
          <template #default="{ row }">
            <el-tag :type="getStatusTag(row.status)">
              {{ getStatusText(row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="createdAt" label="创建时间" width="180">
          <template #default="{ row }">
            {{ formatDate(row.createdAt) }}
          </template>
        </el-table-column>
        <el-table-column prop="note" label="备注" min-width="150">
          <template #default="{ row }">
            {{ row.note || '-' }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="200" fixed="right">
          <template #default="{ row }">
            <el-button type="warning" size="small" @click="handleEdit(row)">
              编辑
            </el-button>
            <el-button 
              type="danger" 
              size="small" 
              @click="handleDelete(row)"
            >
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination">
        <el-pagination
          v-model:current-page="pagination.current"
          v-model:page-size="pagination.size"
          :total="pagination.total"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>

    <!-- 创建/编辑亲友关系对话框 -->
    <el-dialog
      v-model="dialogVisible"
      :title="dialogTitle"
      width="500px"
      @close="resetForm"
    >
      <el-form ref="groupFormRef" :model="groupForm" :rules="groupRules" label-width="100px">
        <el-form-item label="发起用户ID" prop="userId">
          <el-input v-model="groupForm.userId" placeholder="请输入发起用户ID" :disabled="isEditMode" />
        </el-form-item>
        <el-form-item label="目标用户ID" prop="targetUserId">
          <el-input v-model="groupForm.targetUserId" placeholder="请输入目标用户ID" :disabled="isEditMode" />
        </el-form-item>
        <el-form-item label="关系类型" prop="relationshipType">
          <el-select v-model="groupForm.relationshipType" placeholder="请选择关系类型">
            <el-option label="亲友债权人" value="friend_creditor" />
            <el-option label="亲友债务人" value="friend_debtor" />
          </el-select>
        </el-form-item>
        <el-form-item label="分组类型" prop="groupType">
          <el-select v-model="groupForm.groupType" placeholder="请选择分组类型">
            <el-option label="朋友" value="friend" />
            <el-option label="家人" value="family" />
            <el-option label="同事" value="colleague" />
          </el-select>
        </el-form-item>
        <el-form-item label="亲密度" prop="relationshipStrength">
          <el-select v-model="groupForm.relationshipStrength" placeholder="请选择亲密度">
            <el-option label="强" value="strong" />
            <el-option label="中" value="medium" />
            <el-option label="弱" value="weak" />
          </el-select>
        </el-form-item>
        <el-form-item label="备注" prop="note">
          <el-input 
            v-model="groupForm.note" 
            type="textarea" 
            :rows="3" 
            placeholder="请输入备注"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" @click="handleSubmit" :loading="submitting">
            确定
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { 
  Plus, 
  Refresh, 
  Search
} from '@element-plus/icons-vue'
import adminService from '@/api/admin'

// 状态管理
const loading = ref(false)
const dialogVisible = ref(false)
const isEditMode = ref(false)
const submitting = ref(false)
const groupFormRef = ref()
const currentRelationId = ref(null)

// 搜索表单
const searchForm = reactive({
  search: '',
  status: '',
  groupType: ''
})

// 亲友关系表单
const groupForm = reactive({
  userId: '',
  targetUserId: '',
  relationshipType: 'friend_creditor',
  groupType: 'friend',
  relationshipStrength: 'medium',
  note: ''
})

// 表单验证规则
const groupRules = {
  userId: [
    { required: true, message: '请输入发起用户ID', trigger: 'blur' },
    { pattern: /^\d+$/, message: '请输入有效的用户ID', trigger: 'blur' }
  ],
  targetUserId: [
    { required: true, message: '请输入目标用户ID', trigger: 'blur' },
    { pattern: /^\d+$/, message: '请输入有效的用户ID', trigger: 'blur' }
  ],
  relationshipType: [
    { required: true, message: '请选择关系类型', trigger: 'change' }
  ],
  groupType: [
    { required: true, message: '请选择分组类型', trigger: 'change' }
  ]
}

// 分页配置
const pagination = reactive({
  current: 1,
  size: 10,
  total: 0
})

// 亲友关系列表数据
const groupList = ref([])

// 计算属性
const dialogTitle = computed(() => {
  return isEditMode.value ? '编辑亲友关系' : '创建亲友关系'
})

// 方法
const getStatusText = (status) => {
  const map = {
    pending: '待确认',
    accepted: '已接受',
    rejected: '已拒绝'
  }
  return map[status] || status
}

const getRelationshipTypeText = (type) => {
  const map = {
    friend_creditor: '亲友债权人',
    friend_debtor: '亲友债务人'
  }
  return map[type] || type
}

const getGroupTypeText = (type) => {
  const map = {
    friend: '朋友',
    family: '家人',
    colleague: '同事'
  }
  return map[type] || type
}

const getStrengthText = (strength) => {
  const map = {
    strong: '强',
    medium: '中',
    weak: '弱'
  }
  return map[strength] || strength
}

const getStatusTag = (status) => {
  const map = {
    pending: 'warning',
    accepted: 'success',
    rejected: 'danger'
  }
  return map[status] || ''
}

const formatDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleString('zh-CN')
}

const formatAmount = (amount) => {
  if (!amount) return '0.00'
  return parseFloat(amount).toFixed(2)
}

// 获取亲友关系列表
const fetchData = async () => {
  loading.value = true
  try {
    const params = {
      page: pagination.current,
      limit: pagination.size
    }
    if (searchForm.search) params.search = searchForm.search
    if (searchForm.status) params.status = searchForm.status
    if (searchForm.groupType) params.groupType = searchForm.groupType
    
    const response = await adminService.get('/friend-groups/admin/list', { params })
    
    if (response) {
      groupList.value = response.data || []
      pagination.total = response.total || 0
    }
  } catch (error) {
    console.error('获取亲友关系列表失败:', error)
    ElMessage.error('获取亲友关系列表失败')
  } finally {
    loading.value = false
  }
}

const refreshData = () => {
  fetchData()
}

const handleSearch = () => {
  pagination.current = 1
  fetchData()
}

const resetSearch = () => {
  Object.keys(searchForm).forEach(key => {
    searchForm[key] = ''
  })
  handleSearch()
}

const handleSizeChange = (size) => {
  pagination.size = size
  fetchData()
}

const handleCurrentChange = (page) => {
  pagination.current = page
  fetchData()
}

const handleCreateGroup = () => {
  isEditMode.value = false
  currentRelationId.value = null
  dialogVisible.value = true
}

const handleEdit = (row) => {
  isEditMode.value = true
  currentRelationId.value = row.id
  groupForm.userId = row.userId
  groupForm.targetUserId = row.targetUserId
  groupForm.relationshipType = row.relationshipType
  groupForm.groupType = row.groupType
  groupForm.relationshipStrength = row.relationshipStrength
  groupForm.note = row.note || ''
  dialogVisible.value = true
}

const handleDelete = async (row) => {
  try {
    await ElMessageBox.confirm('确定要删除该亲友关系吗？此操作不可恢复。', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    })
    
    await adminService.delete(`/friend-groups/admin/relation/${row.id}`)
    ElMessage.success('亲友关系已删除')
    fetchData()
  } catch (error) {
    if (error !== 'cancel') {
      console.error('删除亲友关系失败:', error)
      ElMessage.error('删除亲友关系失败')
    }
  }
}

const resetForm = () => {
  if (groupFormRef.value) {
    groupFormRef.value.resetFields()
  }
  groupForm.userId = ''
  groupForm.targetUserId = ''
  groupForm.relationshipType = 'friend_creditor'
  groupForm.groupType = 'friend'
  groupForm.relationshipStrength = 'medium'
  groupForm.note = ''
  currentRelationId.value = null
}

const handleSubmit = async () => {
  if (!groupFormRef.value) return
  
  try {
    await groupFormRef.value.validate()
    
    submitting.value = true
    
    if (isEditMode.value) {
      // 更新亲友关系
      await adminService.put(`/friend-groups/admin/relation/${currentRelationId.value}`, {
        relationshipType: groupForm.relationshipType,
        relationshipStrength: groupForm.relationshipStrength,
        note: groupForm.note
      })
      ElMessage.success('亲友关系更新成功')
    } else {
      // 创建亲友关系
      await adminService.post('/friend-groups', {
        targetUserId: parseInt(groupForm.targetUserId),
        relationshipType: groupForm.relationshipType,
        groupType: groupForm.groupType,
        relationshipStrength: groupForm.relationshipStrength,
        note: groupForm.note
      })
      ElMessage.success('亲友关系创建成功')
    }
    
    dialogVisible.value = false
    resetForm()
    fetchData()
  } catch (error) {
    console.error('操作失败:', error)
    ElMessage.error(error.response?.data?.message || '操作失败')
  } finally {
    submitting.value = false
  }
}

// 生命周期
onMounted(() => {
  fetchData()
})
</script>

<style scoped>
.friend-group-management {
  padding: 20px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.header h2 {
  margin: 0;
  font-size: 24px;
  color: #ffffff;
}

.actions {
  display: flex;
  gap: 10px;
}

.filter-card {
  margin-bottom: 20px;
}

.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.user-info {
  display: flex;
  flex-direction: column;
}

.user-mobile {
  font-size: 12px;
  color: #909399;
  margin-top: 2px;
}

.amount {
  font-weight: bold;
  color: #f56c6c;
}
</style>
